// ORA-262 / Sentry ORA-FE-R1, ORA-FE-RR.
//
// In-page translation (Chrome/Google Translate, Safari Translate) rewrites our
// text nodes, wrapping them in `<font>` elements. React still holds references
// to the original nodes, so its commit phase eventually calls
// `parent.removeChild(node)` / `parent.insertBefore(node, ref)` for a node the
// parent no longer owns and the DOM throws `NotFoundError`.
//
// React aborts that commit partway through, which is what makes this fatal
// rather than noisy: the subtree it was deleting stays in the document but is
// detached from the fiber tree. The user is left looking at a page that is
// painted, on top, and completely inert — no handler fires, no re-render can
// remove it. Only a full document reload rebuilds a consistent DOM.
//
// Kept as pure functions with injected storage so the whole recovery decision
// is unit-testable; `ErrorPage` only wires them together.

export const DOM_COMMIT_RECOVERY_STORAGE_KEY = 'oraDomCommitRecovery';

// A hard per-tab budget, deliberately not time-windowed. A window that resets
// the count would let a crash which reproduces every N seconds reload forever
// at one reload per window; a plain counter in `sessionStorage` cannot, because
// reloads preserve it and only closing the tab clears it. The cost is that a
// tab which burns the budget stops self-healing — acceptable, since the crash
// runs at roughly one event per affected user per month.
export const MAX_DOM_COMMIT_RECOVERY_ATTEMPTS = 3;

// Engine-specific wordings for "the node you asked me to remove/move is not
// where you think it is".
const DOM_COMMIT_FAILURE_PATTERNS = [
  // Blink/Chromium.
  /Failed to execute '(?:removeChild|insertBefore)' on 'Node'/i,
  // Gecko, and the tail of the Blink message.
  /The node to be removed is not a child of this node/i,
  /The node before which the new node is to be inserted is not a child of this node/i,
  // WebKit, which says only this much.
  /The object can not be found here/i,
];

// Every match additionally requires the `NotFoundError` name. React propagates
// the original DOMException untouched, so the real thing always carries it,
// while application code that merely quotes a DOM message inside its own error
// ("Saving draft failed: Failed to execute 'removeChild'...") does not — and
// must not cost the user a reload.
const NOT_FOUND_ERROR_PATTERN = /\bNotFoundError\b/i;

// DOMException does not extend Error in every engine, so duck-type on
// name/message instead of relying on `instanceof`.
const toErrorText = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    const { name, message } = error as { name?: unknown; message?: unknown };

    if (typeof message === 'string' && message) {
      return typeof name === 'string' && name ? `${name}: ${message}` : message;
    }
  }

  return String(error);
};

export const isUnrecoverableDomCommitError = (error: unknown): boolean => {
  const text = toErrorText(error);

  if (!NOT_FOUND_ERROR_PATTERN.test(text)) {
    return false;
  }

  return DOM_COMMIT_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
};

// Anything outside `0..MAX` is a corrupt or tampered value rather than
// something we wrote. Treating it as "no attempts yet" is the safe reading: an
// out-of-range count must not be able to permanently disable recovery, and a
// negative one must not be able to extend the budget.
export const parseDomCommitRecoveryAttempts = (raw: string | null): number => {
  if (!raw) {
    return 0;
  }

  const parsed = Number(raw);

  if (
    !Number.isInteger(parsed) ||
    parsed < 0 ||
    parsed > MAX_DOM_COMMIT_RECOVERY_ATTEMPTS
  ) {
    return 0;
  }

  return parsed;
};

// Storage access is wrapped at every level because a privacy-restricted context
// can throw on the `sessionStorage` getter itself, not just on getItem/setItem.
export const readDomCommitRecoveryAttempts = (
  storage: Pick<Storage, 'getItem'> | undefined
): number => {
  if (!storage) {
    return 0;
  }

  try {
    return parseDomCommitRecoveryAttempts(
      storage.getItem(DOM_COMMIT_RECOVERY_STORAGE_KEY)
    );
  } catch {
    return 0;
  }
};

// Returns whether the attempt was actually persisted. The caller MUST NOT
// reload when this is false: without a durable counter the budget resets on
// every load, which turns a deterministic crash into an unbounded reload storm.
// Staying on a broken page is bad; reloading forever is worse.
export const writeDomCommitRecoveryAttempts = (
  storage: Pick<Storage, 'setItem'> | undefined,
  attempts: number
): boolean => {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(DOM_COMMIT_RECOVERY_STORAGE_KEY, String(attempts));
    return true;
  } catch {
    return false;
  }
};

// The tab-scoped store, read defensively: reading `window.sessionStorage` is
// itself a throwing operation when cookies/site data are blocked.
export const getDomCommitRecoveryStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
};
